import { useContext, useEffect, useState } from "react"
import Button from "components/buttons/Button"
import H4 from "components/atoms/H4"
import Modal from "components/molecules/Modal"
import axios from "axios"
import cc from "classcat"
import { loadStripe } from "@stripe/stripe-js/pure"
import { numberWithCommas } from "utils/helpers"

import { LocaleContext, LocationContext } from "../contexts"
import {
  siteCountry,
  siteUrl,
  stripeCheckoutEndpoint,
  stripeKey,
} from "../utils/env"

import ButtonInline from "./ButtonInline"
import CheckboxWithTitle from "./CheckboxWithTitle"
import Price from "./Price"
import { fillWithUtm } from "../utils/url"
import i18nConfig from "../../config/i18n"
import useTranslations from "../hooks/useTranslations"

const OrderModal = ({
  modalIsOpen,
  setModalIsOpen,
  partner,
  fullPrice = 0,
  partnerPrice = 0,
  sku,
  pricingMode,
  duration,
  description,
  name,
}) => {
  const [stripe, setStripe] = useState(null)
  const [quantity, setQuantity] = useState(1)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [showAcceptTerms, setShowAcceptTerms] = useState(false)

  const {
    currency,
    orderModalTitle,
    orderModalDescription,
    orderModalProductName,
    personShortSingle,
    personShortPlural,
    orderModalPriceTotal,
    orderModalTerms1,
    orderModalTerms2,
    orderModalTerms3,
    orderModalTerms4,
    orderModalButton,
    orderModalTermsError1,
    orderModalTermsError2,
    orderModalTermsError3,
    orderModalTermsError4,
    orderModalTermsError5,
    pdfPrivacyPolicyLink,
    pdfShopTermsLink,
    // pageOrderCompletedPartner,
    vatIncluded,
    orderModalInvoiceRequest1,
    orderModalInvoiceRequestLink,
    orderModalInvoiceRequest2,
    orderModalInvoiceRequest3,
  } = useTranslations()

  const { locale } = useContext(LocaleContext)
  const [lang] = locale.split("-")
  const countryOptions = i18nConfig[siteCountry]
  const localeOptions = countryOptions[locale]
  const path = localeOptions.default ? "" : `/${localeOptions.path}`
  const { location } = useContext(LocationContext)

  const initStripeIfNeeded = async () => {
    if (!stripe) {
      const stripeInstance = await loadStripe(stripeKey, { locale: lang })
      setStripe(stripeInstance)
      return stripeInstance
    }
    return stripe
  }

  const price = partner ? partnerPrice : fullPrice

  const redirectToCheckout = async () => {
    // Initialize Stripe right before it's needed
    const stripeInstance = await initStripeIfNeeded()

    const successUrl = fillWithUtm(`${siteUrl}${path}/order-completed`)
    const cancelUrl = fillWithUtm(
      `${location.origin}${location.pathname}#Order`,
    )

    const fetchSession = async () => {
      const apiEndpoint = "/checkout"
      const body = {
        quantity,
        client_reference_id: "UniqueString",
        price: sku,
        mode: pricingMode,
        successUrl,
        cancelUrl,
        locale: lang,
      }
      // const session = await API.post(apiName, apiEndpoint, { body })
      const { data } = await axios.post(
        `${stripeCheckoutEndpoint}${apiEndpoint}`,
        body,
      )
      return data
    }

    const { id } = await fetchSession()

    const { error } = await stripeInstance.redirectToCheckout({ sessionId: id })
    if (error) {
      console.log(error)
    }
  }

  const closeModal = () => {
    setModalIsOpen(false)
  }

  const handleTermsCheckboxChange = (e) => {
    setTermsAccepted(e.target.checked)
    if (e.target.checked) {
      setShowAcceptTerms(false)
    }
  }

  const handleBuyButtonClick = (e) => {
    e.preventDefault()
    if (termsAccepted) {
      redirectToCheckout()
    } else {
      setShowAcceptTerms(true)
    }
  }

  const handleQuantityChange = (operation) => {
    if (operation === "subtract" && quantity > 1) {
      setQuantity(quantity - 1)
    }
    if (operation === "add") {
      setQuantity(quantity + 1)
    }
  }

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal}>
      <div className="max-w-lg">
        <H4 textAlign="center">{orderModalTitle}</H4>
        <Price
          price={numberWithCommas(price.toFixed(2))}
          comment={`${currency}${
            pricingMode === "subscription" ? `/${duration}` : ""
          }`}
          className="mb-0 mt-4"
        />
        <div className="mb-2 text-center text-sm">{vatIncluded}</div>
        <div className="mb-3 flex items-center justify-center space-x-4">
          <p className="m-0 leading-4">{orderModalProductName}&nbsp;</p>
          <ButtonInline
            label="-"
            onClick={() => handleQuantityChange("subtract")}
          />
          <div className="whitespace-nowrap text-2xl font-extrabold">
            {quantity} {quantity === 1 ? personShortSingle : personShortPlural}
          </div>
          <ButtonInline label="+" onClick={() => handleQuantityChange("add")} />
        </div>
        <div className="mb-3">
          <div className="m-0 mb-1 whitespace-nowrap text-center">
            {orderModalPriceTotal}:{" "}
            {numberWithCommas((price * quantity).toFixed(2))} {currency}
            {pricingMode === "subscription" && `/${duration}`}
          </div>
          {pricingMode === "subscription" && (
            <div className="mb-0 text-center text-sm leading-5">
              {description}
              {numberWithCommas((price * quantity * 12).toFixed(2))} {currency})
            </div>
          )}
        </div>
        <div className="mb-3 text-center text-xs italic leading-4">
          {orderModalDescription}
        </div>
        <CheckboxWithTitle
          onChange={handleTermsCheckboxChange}
          checked={termsAccepted}
          title={
            <div>
              {orderModalTerms1}
              <a
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer underline"
                href={pdfShopTermsLink}
              >
                {orderModalTerms2}
              </a>
              {orderModalTerms3}
              <a
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer underline"
                href={pdfPrivacyPolicyLink}
              >
                {orderModalTerms4}
              </a>
            </div>
          }
        />
        <div className="mx-auto w-64">
          <Button
            label={orderModalButton}
            bgStyle="primary"
            onClick={handleBuyButtonClick}
          />
        </div>
        <div className="h-3 w-full" />

        <div className="relative">
          <div
            className={cc([
              "absolute inset-y-1.5 text-center text-sm font-bold leading-5 text-persona-color-1",
              {
                invisible: !showAcceptTerms,
              },
            ])}
          >
            {orderModalTermsError1}
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer underline"
              href={pdfShopTermsLink}
            >
              {orderModalTermsError2}
            </a>
            {orderModalTermsError3}
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer underline"
              href={pdfPrivacyPolicyLink}
            >
              {orderModalTermsError4}
            </a>
            {orderModalTermsError5}
          </div>

          <div
            className={cc([
              "text-center text-sm leading-5 text-primaryAccent",
              {
                invisible: showAcceptTerms,
                hidden: !showAcceptTerms && pricingMode === "subscription",
              },
            ])}
          >
            {orderModalInvoiceRequest1}
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer underline"
              href={`${orderModalInvoiceRequestLink}#sku=${sku}&name=${name}&quantity=${quantity}`}
            >
              {orderModalInvoiceRequest2}
            </a>
            {orderModalInvoiceRequest3}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default OrderModal
